import { Component } from 'react';
import { Helmet } from 'react-helmet-async';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Banner } from '@spotify-internal/encore-web';
import classnames from 'classnames';

import * as Sentry from 'lib/client/sentry/sentry';
import withTranslation from 'lib/shared/i18n/withTranslation';
import { getValidSpotifyUri, isValidSpotifyUri } from 'components/ContentReporting/validation-utils';
import { getCountryCode, getFieldValid, getFieldValue, getUriQueryParam } from 'components/ContentReporting/form-utils';
import { withMarketInfo } from 'context/MarketInfo/MarketInfoProvider';
import {
  ALL_FIELDS_IN_EMPLOYEE_PAYLOAD,
  ALL_FIELDS_IN_EXTERNAL_PAYLOAD,
  Field,
  OptionsTeam,
} from 'components/ContentReporting/ContentPolicyReporting/content-policy-reporting-config';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import Step1Description from 'components/ContentReporting/ContentPolicyReporting/Step1Description';
import Step2ExternalReportedContent from 'components/ContentReporting/ContentPolicyReporting/Step2ExternalReportedContent';
import Step3Success from 'components/ContentReporting/ContentPolicyReporting/Step3Success';
import EmailVerificationDialog from 'components/ContentReporting/EmailVerification/EmailVerificationDialog';
import { sendPageview } from 'utils/metrics';
import { FieldChangeData, FormData, MarketInfoProps, TranslationProps } from 'etc/types';
import {
  ContentType,
  contentTypeFromJSON,
  EmailType,
  EmployeeContentPolicyReportRequest,
  ExternalContentPolicyReportRequest,
  Reason,
  reasonFromJSON,
  Urgency,
  urgencyFromJSON,
} from 'proto/generated/tintin_content_policy';
import { TintinClientProps, withTintinClient } from 'components/ContentReporting/withTintinClient';
import Step2EmployeeReportedContent from 'components/ContentReporting/ContentPolicyReporting/Step2EmployeeReportedContent';
import {
  isPodcastReportingEnabledForMarket,
  isMusicVideosReportingEnabledForMarket,
} from 'components/ContentReporting/market-helper';
import { ContextualInfoProps, withContextualInfo } from 'components/ContentReporting/withContextualInfo';
import { QueryParams } from 'proto/generated/webform_query_params';
import { FASTER_EMAIL_VERIFICATION } from 'lib/shared/config/config';

import { featureFlagsFromUrl } from '../../../components/ContentReporting/feature-flags';

import { Experiments, ErrorMessage, Step } from './utils/types';
import { ACCEPTED_URI_TYPES_ALL } from './utils/constants';
import { removeFullTranslationKey } from './utils/functions';

interface ContentPolicyReportingProps
  extends RouteComponentProps,
    MarketInfoProps,
    TranslationProps,
    TintinClientProps,
    ContextualInfoProps {
  formData: FormData;
  transitionTime?: number;
}

interface VerificationDetails {
  resourceUri?: string;
  code?: string;
}

export const buildExternalRequest = (
  fieldValue: (field: Field) => string,
  market: string,
  experiments?: Array<Experiments>,
  queryParams?: QueryParams,
): ExternalContentPolicyReportRequest => {
  const productUri: string = getValidSpotifyUri(fieldValue(Field.REPORTED_CONTENT_URIS));
  const contentType: ContentType = contentTypeFromJSON(fieldValue(Field.REPORTED_CONTENT_TYPE));
  const reason: Reason = reasonFromJSON(fieldValue(Field.REASON));

  return {
    verificationDetails: {
      resourceUri: fieldValue(Field.VERIFICATION_RESOURCE_URI),
      code: fieldValue(Field.VERIFICATION_CODE),
      googleRecaptchaToken: fieldValue(Field.VERIFICATION_CAPTCHA_TOKEN),
    },
    report: {
      reporterEmail: fieldValue(Field.REPORTER_EMAIL),
      reporterCountryCode: getCountryCode(market),
      productUri: productUri,
      contentType: contentType,
      reason: reason,
      experiments: experiments || [],
      reporterUserAgent: '',
      queryParams: queryParams,
    },
  };
};

export const buildEmployeeRequest = (
  fieldValue: (field: Field) => string,
  market: string,
  experiments?: Array<Experiments>,
  queryParams?: QueryParams,
): EmployeeContentPolicyReportRequest => {
  const productUri: string = getValidSpotifyUri(fieldValue(Field.REPORTED_CONTENT_URIS));
  const contentType: ContentType = contentTypeFromJSON(fieldValue(Field.REPORTED_CONTENT_TYPE));
  const team: string =
    fieldValue(Field.TEAM) === OptionsTeam.OTHER ? fieldValue(Field.TEAM_OTHER) : fieldValue(Field.TEAM);
  const urgency: Urgency = urgencyFromJSON(removeFullTranslationKey(fieldValue(Field.URGENCY)));
  const additionalInfo: string = fieldValue(Field.ADDITIONAL_INFO);
  const reason: Reason = reasonFromJSON(fieldValue(Field.REASON));

  return {
    verificationDetails: {
      resourceUri: fieldValue(Field.VERIFICATION_RESOURCE_URI),
      code: fieldValue(Field.VERIFICATION_CODE),
      googleRecaptchaToken: '',
    },
    report: {
      reporterEmail: fieldValue(Field.REPORTER_EMAIL),
      reporterCountryCode: getCountryCode(market),
      productUri: productUri,
      contentType: contentType,
      team: team,
      urgency: urgency,
      additionalInfo: additionalInfo,
      reason: reason,
      experiments: experiments || [],
      reporterUserAgent: '',
      queryParams: queryParams,
    },
  };
};

interface ContentPolicyReportingState {
  currentStep: Step;
  formData: FormData;
  shouldDisplayEmailVerificationFlow: boolean;
  forceValidation: boolean;
  formSubmitting: boolean;
  formSubmitted: boolean;
  formSubmissionError: ErrorMessage | '';
  formSubmissionSuccess: boolean;
  loading: boolean;
  verificationDetails: VerificationDetails;
  enabledFeatureFlags: Set<String>;
}

export const validateFieldsOnSubmit = (
  fields: Array<Field>,
  fieldValue: Function,
  fieldValid: Function,
): Array<Field> => {
  const isValuePresent: (field: Field) => boolean = (field: Field) => !!fieldValue(field);
  const isValueValid: (field: Field) => boolean = (field: Field) => fieldValid(field);
  const invalidFields: Array<Field> = fields.filter((field: Field) => {
    return !isValuePresent(field) || !isValueValid(field);
  });
  return invalidFields;
};

class ContentPolicyReporting extends Component<ContentPolicyReportingProps, ContentPolicyReportingState> {
  static defaultProps = {
    formData: {},
  };

  constructor(props: ContentPolicyReportingProps) {
    super(props);
    const uriQueryParam = getUriQueryParam(props.location) as string;
    this.state = {
      currentStep: Step.STEP1_DESCRIPTION,
      formData: {
        ...this.props.formData,
        [Field.REPORTED_CONTENT_URIS]: {
          value: uriQueryParam,
          valid: isValidSpotifyUri(uriQueryParam, ACCEPTED_URI_TYPES_ALL),
        },
      },
      shouldDisplayEmailVerificationFlow: false,
      forceValidation: false,
      formSubmitting: false,
      formSubmitted: false,
      formSubmissionError: '',
      formSubmissionSuccess: false,
      loading: false,
      verificationDetails: {},
      enabledFeatureFlags: featureFlagsFromUrl(),
    };

    Sentry.setTag('app', 'ContentPolicyReporting');
    sendPageview(`content-policy-reporting`);
  }

  handleFieldChange = ({ fieldIdentifier, value, valid }: FieldChangeData) => {
    this.setState(
      (prevState: Readonly<ContentPolicyReportingState>): ContentPolicyReportingState => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          [fieldIdentifier]: {
            value,
            valid,
          },
        },
        formSubmissionError: valid ? '' : prevState.formSubmissionError,
      }),
    );
  };

  handleExternalFormSubmit = async (formData: FormData, market: string): Promise<void> => {
    const fieldValue: (field: Field) => string = getFieldValue(formData);
    const fieldValid: (field: Field) => boolean = getFieldValid(formData);
    const invalidFields = validateFieldsOnSubmit(ALL_FIELDS_IN_EXTERNAL_PAYLOAD, fieldValue, fieldValid);
    if (invalidFields.length === 0) {
      const { client, platform, context } = this.props;

      const queryParams = {
        platform,
        contextUri: context.contextUri?.toURI(),
        playbackTimestamp: context.playbackTimestamp,
      };
      const request: ExternalContentPolicyReportRequest = buildExternalRequest(
        fieldValue,
        market,
        undefined,
        queryParams,
      );

      this.setState({ loading: true });
      try {
        await client.submitExternalContentPolicyReport(request);
        this.onSuccess();
      } catch (e) {
        this.onError(ErrorMessage.GENERIC);
      }
    } else {
      this.onInvalidFields(invalidFields);
      this.onError(ErrorMessage.FORM_INVALID);
    }
  };

  handleEmployeeFormSubmit = async (formData: FormData, market: string): Promise<void> => {
    const fieldValue: (field: Field) => string = getFieldValue(formData);
    const fieldValid: (field: Field) => boolean = getFieldValid(formData);
    const invalidFields = validateFieldsOnSubmit(ALL_FIELDS_IN_EMPLOYEE_PAYLOAD, fieldValue, fieldValid);
    if (invalidFields.length === 0) {
      const { client, platform, context } = this.props;

      const queryParams = {
        platform,
        contextUri: context.contextUri?.toURI(),
        playbackTimestamp: context.playbackTimestamp,
      };
      const request: EmployeeContentPolicyReportRequest = buildEmployeeRequest(
        fieldValue,
        market,
        undefined,
        queryParams,
      );

      this.setState({ loading: true });
      try {
        await client.submitEmployeeContentPolicyReport(request);
        this.onSuccess();
      } catch (e) {
        this.onError(ErrorMessage.GENERIC);
      }
    } else {
      this.onInvalidFields(invalidFields);
      this.onError(ErrorMessage.FORM_INVALID);
    }
  };

  handleCancelEmailVerification = () => {
    this.setState({
      currentStep: Step.STEP1_DESCRIPTION,
      shouldDisplayEmailVerificationFlow: false,
    });
  };

  handleCaptchaChange = (token: string | null) => {
    const valid: boolean = !!token;
    const value: string = token || '';
    // If the reCAPTCHA challenge expires the token we receive will be null.
    // Make sure we invalidate the form data for the CAPTCHA token in that case.
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [Field.VERIFICATION_CAPTCHA_TOKEN]: {
          value: value,
          valid: valid,
        },
      },
    }));
  };

  onSuccess = () => {
    this.setState({
      loading: false,
      formSubmissionError: '',
      formSubmissionSuccess: true,
    });
  };

  onError = (error: ErrorMessage) => {
    this.setState({
      loading: false,
      formSubmissionError: error,
      forceValidation: true,
    });
  };

  onInvalidFields = (invalidFields: Array<Field>) => {
    invalidFields.forEach((field: Field) =>
      this.setState(
        (prevState: Readonly<ContentPolicyReportingState>): ContentPolicyReportingState => ({
          ...prevState,
          formData: {
            ...prevState.formData,
            [field]: {
              valid: false,
              value: prevState.formData[field] ? prevState.formData[field].value : '',
            },
          },
        }),
      ),
    );
  };

  onClickDescriptionShowEmailModal = () => {
    this.setState({ shouldDisplayEmailVerificationFlow: true });
  };

  onClickExternalReportedContentNextStep = () => {
    const { formData } = this.state;
    const { market } = this.props.marketInfo;
    this.handleExternalFormSubmit(formData, market);
  };

  onClickEmployeeReportedContentNextStep = () => {
    const { formData } = this.state;
    const { market } = this.props.marketInfo;
    this.handleEmployeeFormSubmit(formData, market);
  };

  onSuccessfulEmailVerification = (email: string, resourceUri: string, code: string, emailType: EmailType) => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [Field.REPORTER_EMAIL]: {
          value: email,
          valid: true,
        },
        [Field.VERIFICATION_RESOURCE_URI]: {
          value: resourceUri,
          valid: true,
        },
        [Field.VERIFICATION_CODE]: {
          value: code,
          valid: true,
        },
      },
    }));

    this.goToReportedContentStep(emailType);
  };

  goToReportedContentStep = (emailType: EmailType) => {
    this.setState({ shouldDisplayEmailVerificationFlow: false });
    if (emailType === EmailType.EMAIL_TYPE_SPOTIFY) {
      this.goToNextStep(Step.STEP2_EMPLOYEE_REPORTED_CONTENT);
    } else {
      this.goToNextStep(Step.STEP2_EXTERNAL_REPORTED_CONTENT);
    }
  };

  goToNextStep = (nextStep: Step) => {
    this.setState({
      currentStep: nextStep,
      formSubmissionError: '',
      forceValidation: false,
    });
  };

  setLoading = (loading: boolean) => {
    this.setState({ loading: loading });
  };

  renderErrorMessage = () => {
    const { formSubmissionError, loading } = this.state;
    const { t } = this.props;

    if (formSubmissionError && !loading) {
      return (
        <>
          <Banner colorSet="negative">{t(formSubmissionError)}</Banner>
          <br />
        </>
      );
    }

    return null;
  };

  render() {
    const {
      t,
      transitionTime,
      client,
      location,
      marketInfo: { market },
    } = this.props;

    const {
      formData,
      currentStep,
      shouldDisplayEmailVerificationFlow,
      forceValidation,
      formSubmissionSuccess,
      loading,
    } = this.state;

    const fieldValue: (identifier: string) => string = getFieldValue(formData);
    const fieldValid: (identifier: string) => boolean = getFieldValid(formData);
    const isUserMarketEnabledForPodcastReporting = isPodcastReportingEnabledForMarket(market);
    const isUserMarketEnabledForMusicVideosReporting = isMusicVideosReportingEnabledForMarket(market);

    const getStepComponent = (step: Step) => {
      switch (step) {
        case Step.STEP2_EXTERNAL_REPORTED_CONTENT:
          return (
            <Step2ExternalReportedContent
              onStepSubmit={this.onClickExternalReportedContentNextStep}
              onFieldChange={this.handleFieldChange}
              forceValidation={forceValidation}
              onCaptchaChange={this.handleCaptchaChange}
              fieldValue={fieldValue}
              fieldValid={fieldValid}
              loading={loading}
              location={location}
              setLoading={this.setLoading}
            />
          );
        case Step.STEP2_EMPLOYEE_REPORTED_CONTENT:
          return (
            <Step2EmployeeReportedContent
              onStepSubmit={this.onClickEmployeeReportedContentNextStep}
              onFieldChange={this.handleFieldChange}
              forceValidation={forceValidation}
              fieldValue={fieldValue}
              fieldValid={fieldValid}
              loading={loading}
              location={location}
              setLoading={this.setLoading}
            />
          );
        default:
          return (
            <Step1Description
              onStepSubmit={this.onClickDescriptionShowEmailModal}
              onFieldChange={this.handleFieldChange}
              forceValidation={forceValidation}
              loading={loading}
              location={location}
              isPodcastReportingEnabled={isUserMarketEnabledForPodcastReporting}
              isMusicVideosReportingEnabled={isUserMarketEnabledForMusicVideosReporting}
              setLoading={this.setLoading}
            />
          );
      }
    };

    const wasFormSubmittedWithSuccess: boolean = formSubmissionSuccess && !loading;

    return (
      <ErrorBoundary>
        <div className={classnames('container')}>
          <Helmet>
            <title>{t('main_title')}</title>
            <meta name="robots" content="index, follow" />
          </Helmet>

          {wasFormSubmittedWithSuccess ? (
            <Step3Success location={location} />
          ) : (
            <>
              {this.renderErrorMessage()}
              {getStepComponent(currentStep)}
            </>
          )}
          {shouldDisplayEmailVerificationFlow && (
            <EmailVerificationDialog
              onSuccessfulFinish={this.onSuccessfulEmailVerification}
              client={client}
              onCancel={this.handleCancelEmailVerification}
              timeout={FASTER_EMAIL_VERIFICATION ? 0 : transitionTime}
            />
          )}
        </div>
      </ErrorBoundary>
    );
  }
}

export default withContextualInfo(
  withTranslation('content_policy_reporting')(withMarketInfo(withTintinClient(withRouter(ContentPolicyReporting)))),
);
